import { createAsyncThunk } from '@reduxjs/toolkit';

import { GLOBAL_CATALOG_ITEMS_PAGE_SIZE } from 'entities/CatalogItem/model/consts/catalogItemsConsts';
import { getSelectedCategoryIdToFetch } from 'entities/Category/model/selectors/categorySelectors';
import {
    getSelectedPropertyCatalogId,
    getSelectedPropertyId,
} from 'entities/Property/model/selectors/PropertySelectors';
import { getAddCatalogItemMode } from 'features/AddCatalogItem/model/selectors/addCatalogItemSelectors';
import {
    getSearchCatalogItemIsActive,
    getSearchGlobalCatalogItemValue,
    getSearchLocalCatalogItemValue,
} from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import { getCatalogSearchRouteContext } from 'shared/lib/searchVersionPreference/isCatalogSearchRoute';

import { fetchCatalogItems } from '../fetchCatalogItems/fetchCatalogItems';
import { fetchGlobalCatalogItems } from '../fetchGlobalCatalogItems/fetchGlobalCatalogItems';

import type { ThunkConfig } from 'app/providers/StoreProvider/config/types';

export const refreshCatalogSearchResults = createAsyncThunk<
    void,
    void,
    ThunkConfig<string>
>('catalogItem/refreshCatalogSearchResults', async (_, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const routeContext = getCatalogSearchRouteContext(window.location.pathname);

    if (!routeContext) {
        return;
    }

    const state = getState();
    const isActiveSearch = getSearchCatalogItemIsActive(state);
    const globalSearchValue = getSearchGlobalCatalogItemValue(state);
    const localSearchValue = getSearchLocalCatalogItemValue(state);
    const selectedCategoryId = getSelectedCategoryIdToFetch(state);
    const selectedPropertyCatalogId = getSelectedPropertyCatalogId(state);
    const selectedPropertyId = getSelectedPropertyId(state);
    const isEditingCatalog = getAddCatalogItemMode(state);

    const fetchGlobalResults = async (searchString?: string) => {
        await dispatch(
            fetchGlobalCatalogItems({
                ...(searchString && { searchString }),
                categoryId: selectedCategoryId,
                page: 1,
                pageSize: GLOBAL_CATALOG_ITEMS_PAGE_SIZE,
                propertyId: selectedPropertyId,
            }),
        );
    };

    if (routeContext.type === 'global') {
        const trimmedSearch = globalSearchValue?.trim() ?? '';

        if (isActiveSearch && trimmedSearch) {
            await fetchGlobalResults(trimmedSearch);
            return;
        }

        if (selectedCategoryId) {
            await fetchGlobalResults();
        }

        return;
    }

    if (!selectedPropertyCatalogId) {
        return;
    }

    const trimmedSearch = localSearchValue?.trim() ?? '';

    if (isActiveSearch && trimmedSearch) {
        await dispatch(
            fetchCatalogItems({
                data: {
                    catalogId: selectedPropertyCatalogId,
                    searchStr: trimmedSearch,
                    ...(routeContext.isInventory && {
                        isInventory: true,
                    }),
                },
            }),
        );
        return;
    }

    if (selectedCategoryId) {
        if (isEditingCatalog) {
            await fetchGlobalResults();
            return;
        }

        await dispatch(
            fetchCatalogItems({
                data: {
                    catalogId: selectedPropertyCatalogId,
                    categoryId: selectedCategoryId,
                    ...(routeContext.isInventory && {
                        isInventory: true,
                    }),
                },
            }),
        );
    }
});
